import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import { createTaskEvents } from '../utils/api';


const ClientExtensionContext = createContext(null);

const reportHandlerError = (endpoint, error) => {
  console.error(`Client extension endpoint "${endpoint}" failed:`, error);
};

export const ClientExtensionProvider = ({ children }) => {
  const handlersRef = useRef(new Map());

  const registerClientExtension = useCallback((endpoint, handler) => {
    if (typeof endpoint !== 'string' || !endpoint || typeof handler !== 'function') {
      throw new Error('Client extensions require a nonempty endpoint and handler.');
    }

    handlersRef.current.set(endpoint, handler);
    return () => {
      if (handlersRef.current.get(endpoint) === handler) {
        handlersRef.current.delete(endpoint);
      }
    };
  }, []);

  const deliverClientExtension = useCallback((delivery, metadata) => {
    if (delivery?.runtime !== 'client') return;

    const endpoint = delivery?.endpoint;
    const handler = handlersRef.current.get(endpoint);
    if (!handler) {
      console.warn(`No client extension is registered for endpoint "${endpoint || ''}".`);
      return;
    }

    try {
      const result = handler(delivery, metadata);
      if (result && typeof result.catch === 'function') {
        result.catch((error) => reportHandlerError(endpoint, error));
      }
    } catch (error) {
      reportHandlerError(endpoint, error);
    }
  }, []);

  const emitTaskEvents = useCallback((taskId, eventTypes, { assignedTaskId = null } = {}) => {
    const request = createTaskEvents(taskId, eventTypes, assignedTaskId)
      .then((response) => {
        const deliveries = Array.isArray(response?.deliveries) ? response.deliveries : [];
        const endpointTotals = new Map();
        deliveries.forEach((delivery) => {
          if (delivery?.runtime !== 'client') return;
          endpointTotals.set(delivery.endpoint, (endpointTotals.get(delivery.endpoint) || 0) + 1);
        });
        const endpointIndexes = new Map();
        deliveries.forEach((delivery) => {
          const endpointIndex = (endpointIndexes.get(delivery?.endpoint) || 0) + 1;
          endpointIndexes.set(delivery?.endpoint, endpointIndex);
          deliverClientExtension(delivery, {
            index: endpointIndex - 1,
            isLastForEndpoint: endpointIndex === endpointTotals.get(delivery?.endpoint),
            totalForEndpoint: endpointTotals.get(delivery?.endpoint) || 0,
          });
        });
        return response;
      })
      .catch((error) => {
        console.error('Failed to deliver task events:', error);
        return null;
      });

    return request;
  }, [deliverClientExtension]);

  const value = useMemo(() => ({
    emitTaskEvents,
    registerClientExtension,
  }), [emitTaskEvents, registerClientExtension]);

  return (
    <ClientExtensionContext.Provider value={value}>
      {children}
    </ClientExtensionContext.Provider>
  );
};

export const useClientExtensions = () => {
  const context = useContext(ClientExtensionContext);
  if (!context) {
    throw new Error('useClientExtensions must be used within ClientExtensionProvider');
  }
  return context;
};
